import { Box, Card, IconButton, Spinner } from "@chakra-ui/react";
import { Table as ChakraTable } from "@chakra-ui/react";
import { Trash2 } from "lucide-react";

import Footer from "@/components/Footer";
import { toaster } from "@/components/ui/toaster";
import { SUBSCRIPTIONS_GUEST_TABLE_COLUMNS } from "@/config/tables";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import Filters from "./Filters";
import { useFetchSubscriptionGuests } from "../hooks/useFetchSubscriptionGuests";
import { useRemoveSubscriptionGuest } from "../hooks/useRemoveSubscriptionGuest";
import { FiltersType } from "../types/filters.type";
import GenericTable from "@/components/GenericTable";
import GenericTableElement from "@/components/GenericTableElement";

const TABLE_KEY = "subscriptions-guests";

const columns = [
  ...SUBSCRIPTIONS_GUEST_TABLE_COLUMNS,
  { key: "actions", label: "", minWidth: 50 },
] as const;

export default function GuestsTable() {
  const { queryFilters, setQueryFilters, cursor, setCursor } =
    useTableFiltersCursor<FiltersType>(TABLE_KEY);
  const { guests, nextCursor, isLoading } = useFetchSubscriptionGuests(
    queryFilters,
    cursor
  );
  const { removeGuest, isRemoving, removingDocumentNumber } =
    useRemoveSubscriptionGuest(
      () => {
        toaster.create({
          title: "Invitado eliminado",
          description: "El invitado ha sido eliminado exitosamente.",
          type: "success",
          duration: 5000,
        });
      },
      () => {
        toaster.create({
          title: "Error",
          description: "No se pudo eliminar el invitado.",
          type: "error",
          duration: 5000,
        });
      }
    );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="1.5rem"
      flex="1"
      minH={0}
    >
      <Card.Root
        borderRadius="lg"
        border="none"
        display="flex"
        flexDirection="column"
        gap="1rem"
        flexShrink={0}
        boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      >
        <Card.Body p="4">
          <Filters
            queryFilters={queryFilters}
            setQueryFilters={setQueryFilters}
          />
        </Card.Body>
      </Card.Root>
      <GenericTable isLoading={isLoading} columns={columns} fillHeight>
        {guests.map((guest, index) => (
          <ChakraTable.Row key={guest.documentNumber}>
            {columns.map((column) =>
              column.key === "actions" ? (
                <GenericTableElement
                  key={`${index}-${column.key}`}
                  value={undefined}
                  actions={
                    <IconButton
                      aria-label="Eliminar invitado"
                      variant="ghost"
                      size="xs"
                      colorPalette="red"
                      onClick={() => removeGuest(guest.documentNumber)}
                      disabled={isRemoving}
                    >
                      {isRemoving &&
                      removingDocumentNumber === guest.documentNumber ? (
                        <Spinner size="sm" />
                      ) : (
                        <Trash2 size="1.2rem" />
                      )}
                    </IconButton>
                  }
                />
              ) : (
                <GenericTableElement
                  key={`${index}-${column.key}`}
                  value={guest[column.key]}
                  formatDatetimeColombian={column.key === "subscriptionSince"}
                />
              )
            )}
          </ChakraTable.Row>
        ))}
      </GenericTable>
      <Footer
        tableProps={{
          fileName: "invitados",
          items: guests,
          columns: SUBSCRIPTIONS_GUEST_TABLE_COLUMNS,
          pagination: { tableKey: TABLE_KEY, nextCursor, setCursor, cursor },
        }}
      />
    </Box>
  );
}
